import { trackEvent, trackProps } from '../../helpers/utils/analytics';
import { useContext } from 'react';
import { Text, Stack, Box, Flex } from '../ui';
import { Radio, RadioGroup } from '../ui/radio';
import { HelpCircle } from 'lucide-react';
import {
  HELPS_TEAM_BID,
  NO_BAGS_NO_HELP,
  TAKES_BAGS,
} from '../../helpers/utils/constants';
import { GlobalContext } from '../../store/global-context';
import type { NilSetting } from '../../types';

interface ScoreSettingProps {
  onOpenScoreHelp?: () => void;
  /** Desktop Home's Game setup rail reports its changes as `setup` (C8). */
  analyticsContext?: 'setup';
}

function ScoreSetting({
  onOpenScoreHelp,
  analyticsContext,
}: ScoreSettingProps) {
  const { nilScoringRule, setNilScoringRule, role } = useContext(GlobalContext);

  // The nil scoring rule is a leader-owned game-logic setting. A viewer sees
  // scores already computed with the leader's rule (hydrated), so letting them
  // change it locally would silently diverge from the shared board.
  if (role === 'viewer') return null;

  const handleClick = () => {
    if (onOpenScoreHelp) {
      onOpenScoreHelp();
    }
  };
  return (
    <div className="failed-nil-setting">
      <Flex
        align="center"
        gap="var(--spades-spacing-sm)"
        mt="var(--modal-setting-gap)"
      >
        <Text className="setting-label">Failed nil</Text>
        <HelpCircle
          size={18}
          {...trackProps('score_help_opened')}
          onClick={handleClick}
          data-testid="score-help-button"
          style={{
            cursor: 'pointer',
            verticalAlign: 'middle',
            display: 'inline',
            position: 'relative',
            top: '-2px',
          }}
        />
      </Flex>
      <Text
        fontSize="var(--spades-font-size-sm)"
        mt="var(--spades-spacing-sm)"
        mb="var(--modal-action-gap)"
        lineHeight={1.25}
      >
        Select your preferred scoring rules for failed nil.
      </Text>
      <RadioGroup
        onValueChange={(e: { value: string | null }) => {
          const value = (e.value || TAKES_BAGS) as NilSetting;
          if (value === nilScoringRule) return;
          setNilScoringRule(value);
          trackEvent('nil_scoring_rule_changed', {
            value:
              value === TAKES_BAGS
                ? 'takes_bags'
                : value === HELPS_TEAM_BID
                  ? 'helps_team_bid'
                  : 'no_bags_no_help',
            ...(analyticsContext && { context: analyticsContext }),
          });
        }}
        value={nilScoringRule}
      >
        <Stack
          className="nil-rule-options"
          alignItems="stretch"
          gap="var(--modal-action-gap)"
        >
          <Radio value={TAKES_BAGS} inputProps={{ 'aria-label': 'Takes Bags' }}>
            <Box fontWeight="800">Takes Bags</Box>
            <Box className="nil-rule-desc">Nil’s tricks become team bags</Box>
          </Radio>
          <Radio
            value={HELPS_TEAM_BID}
            inputProps={{ 'aria-label': 'Helps Team Bid' }}
          >
            <Box fontWeight="800">Helps Team Bid</Box>
            <Box className="nil-rule-desc">
              Nil’s tricks count toward partner’s bid
            </Box>
          </Radio>
          <Radio
            value={NO_BAGS_NO_HELP}
            inputProps={{ 'aria-label': 'No Bags/No Help' }}
          >
            <Box fontWeight="800">No Bags/No Help</Box>
            <Box className="nil-rule-desc">Nil’s tricks don’t count at all</Box>
          </Radio>
        </Stack>
      </RadioGroup>
    </div>
  );
}

export default ScoreSetting;
